import { useEffect, useState } from 'react';
import { DashboardCard } from '../../../shared/types';
import Header from '../../../header';
import Loading from '../../Loading';
import { useAuth } from '../../../contexts/AuthContext';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { apiGet, apiPatch } from '../../../utils/api';
import { Card } from '../../Card';
import AdminHeader from './AdminHeader';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';

const DashboardConfig = () => {
  const [ dashboardCards, setDashboardCards ] = useState<DashboardCard[]>([]);
  const [ isLoading, setIsLoading ] = useState<boolean>(true);
  const [ pendingChanges, setPendingChanges ] = useState<{ [key: string]: boolean }>({});
  const [ isSaving, setIsSaving ] = useState<boolean>(false);
  const { user } = useAuth();
  const organization = useCurrentOrganization();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const cardsResponse = await apiGet(`/api/organizations/${organization.id}/dashboard-cards`);
        if (cardsResponse.ok) {
          const cards = await cardsResponse.json();
          setDashboardCards(cards);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) fetchSettings();
  }, [ organization.id, user ]);

  const handleCardToggle = (cardId: string, enabled: boolean) => {
    setPendingChanges(prev => ({
      ...prev,
      [ cardId ]: enabled
    }));
  };

  const handleSave = async () => {
    if (Object.keys(pendingChanges).length === 0) { return; }
    setIsSaving(true);
    try {
      const newCards = dashboardCards.map(card => ({
        ...card,
        enabled: pendingChanges[card.id] ?? card.enabled
      }));

      const response = await apiPatch(`/api/organizations/${organization.id}/dashboard-cards`, { dashboardCards: newCards });

      if (response.ok) {
        setDashboardCards(newCards);
        setPendingChanges({});
      } else {
        console.error('Error saving changes:', response.statusText);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = Object.keys(pendingChanges).length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <Loading message="Loading Dashboard Configuration..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header isAdminPage={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader
          title="Dashboard Configuration"
          description="Customize which information cards are displayed on your patient dashboard"
          organization={organization}
        />

        <Card className="w-full p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Cog6ToothIcon className="w-6 h-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-gray-900">Dashboard Cards</h2>
            </div>
            {hasUnsavedChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white font-medium py-2 px-6 rounded-md transition-colors flex items-center gap-2"
              >
                {isSaving ? (
                  <Loading />
                ) : (
                  'Save Changes'
                )}
              </button>
            )}
          </div>
          <p className="text-gray-600 mb-8">
            Customize which information cards are displayed on your patient dashboard.
            <span className="block mt-2 text-sm text-yellow-600 font-medium">
              ⚠️ Only organization administrators can modify these settings.
            </span>
            {hasUnsavedChanges && (
              <span className="block mt-2 text-sm text-brand-600 font-medium">
                💾 You have unsaved changes. Click "Save Changes" to apply them.
              </span>
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardCards.map((card) => {
              const pendingValue = pendingChanges.hasOwnProperty(card.id)
                ? pendingChanges[card.id]
                : card.enabled;

              return (
                <div key={card.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{card.title}</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pendingValue}
                        onChange={(e) => handleCardToggle(card.id, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{card.description}</p>
                  {pendingChanges.hasOwnProperty(card.id) && (
                    <div className="text-xs text-brand-600 font-medium">
                      ⚠️ Pending change
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardConfig;
